import { css } from '@emotion/react';
import { Operatory } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/operatory.pb';
import { useTranslation } from '@frontend/i18n';
import { useIsSmallMode } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Accordion, Button, Text, useModalControl, Truncated } from '@frontend/design-system';
import { OperatoryAvatar } from '../ManageOperatoryV3/operatory-avatar';
import { AvailabilityTable } from './availability-table';
import { AvailabilityTray } from './availability-tray';

type OperatoryContainerProps = {
  operatory: Operatory;
  locationId: string;
};

export const OperatoryContainer = ({ operatory, locationId }: OperatoryContainerProps) => {
  const { t } = useTranslation('scheduleGlobalSettings');
  const { isSmallMode } = useIsSmallMode();

  const { modalProps, triggerProps } = useModalControl();

  //TODO - replace with real count when api is available
  const count = 0;

  if (!operatory) return null;

  return (
    <section css={containerStyles}>
      <header css={headerStyles(isSmallMode, !!operatory.displayName)}>
        <div css={avatarColumnStyles}>
          <OperatoryAvatar
            avatarInfo={operatory?.details?.icon}
            operatoryName={operatory.externalName || operatory.displayName || ''}
            disabled={!operatory.details?.act}
          />
        </div>
        <div
          css={{
            gridColumn: '2',
            gridRow: '1',
            display: 'flex',
            flexDirection: isSmallMode ? 'column' : 'row',
            gap: theme.spacing(1),
          }}
        >
          {operatory.displayName && (
            <Truncated css={[textStyles(!!operatory.details?.act), displayNameStyles]}>
              {operatory.displayName}
            </Truncated>
          )}
          {operatory.externalName && (
            <Truncated
              css={[textStyles(!!operatory.details?.act), externalNameStyles(isSmallMode, !!operatory.displayName)]}
            >
              {operatory.externalName}
            </Truncated>
          )}
        </div>
      </header>
      {operatory.details?.act && (
        <Accordion variant='blank'>
          <Accordion.Item value='schedule definition templates' css={{ borderRadius: theme.borderRadius.medium }}>
            <Accordion.Header>
              <Text>{t('Availability')}</Text>
              <Text css={{ marginLeft: theme.spacing(1), color: theme.colors.neutral60 }}>{count}</Text>
            </Accordion.Header>
            <Accordion.Body css={{ padding: 0 }}>
              <AvailabilityTable />
              <Button variant='tertiary' iconName='plus-small' css={{ margin: theme.spacing(1.5) }} {...triggerProps}>
                {t('Add Availability')}
              </Button>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      )}
      <AvailabilityTray modalProps={modalProps} operatory={operatory} locationId={locationId} />
    </section>
  );
};

const containerStyles = {
  marginBottom: theme.spacing(4),
  border: `1px solid ${theme.colors.neutral30}`,
  borderRadius: theme.borderRadius.medium,
};

const headerStyles = (isSmallMode: boolean, hasDisplayName: boolean) =>
  css({
    display: 'grid',
    gridTemplateColumns: 'minmax(32px, 64px) auto',
    gridTemplateRows: hasDisplayName && isSmallMode ? 'auto auto' : 'auto',
    justifyContent: 'start',
    alignItems: 'center',
    padding: theme.spacing(1.25, 2),
    borderBottom: `1px solid ${theme.colors.neutral20}`,
  });

const avatarColumnStyles = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gridRow: '1 / -1',
};

const displayNameStyles = {
  gridColumn: '2',
  gridRow: '1',
};

const externalNameStyles = (isSmallMode: boolean, hasDisplayName: boolean) => ({
  gridColumn: '2',
  gridRow: hasDisplayName && isSmallMode ? '2' : '1',
});

const textStyles = (isActive: boolean) => ({
  color: isActive ? theme.colors.neutral90 : theme.colors.neutral40,
  minWidth: 180,
  maxWidth: 240,
  marginLeft: theme.spacing(1),
});
